/**
 * RISK-EDIT-CARD-OPEN
 * Form to edit risk in the "updates" screen
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle, faSpinner, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons"
import { FadeIn } from "@/utils/animations.utils"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import RiskScaleSelector from "@/features/risks/entities/risk/ui/RiskScaleSelector"
import Tooltip from "@/components/tooltip"
import Chip from "@/components/chip"
import SaveToast from "@/components/SaveToast"
import Modal from "@/components/modal"
import { t } from "i18next"
import { getRiskHelpText } from "@/features/risks/entities/risk/utils/getRiskHelpText"
import { useMemo, useState } from "react"
import RiskDeleteModal from "@/features/risks/entities/risk/ui/modals/RiskDeleteModal"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import { OwlRunner } from "@/components/OwlRunner"
import { useNavigate, useLocation } from "react-router-dom"
import RiskHistorySideNav from "./modals/RiskHistorySideNav"
import RiskSuggestionModal from "./modals/RiskSuggestionModal"
import Checkbox from "@/components/checkbox"
import { toast } from "react-toastify"
import Card from "@/components/card"
import RiskTypeChip from "@/features/risks/entities/risk/ui/RiskTypeChip"
import UnitBadge from "@/features/risks/entities/risk/ui/UnitBadge"
import Dropdown from "@/components/dropdown"
import TextInput from "@/components/text-input"

const MODAL_DELETE = "MODAL_DELETE"
const MODAL_HISTORY = "MODAL_HISTORY"
const MODAL_SCALE_CONFIG = "MODAL_SCALE_CONFIG"
const MODAL_CONFIRM_ASSOCIATED = "MODAL_CONFIRM_ASSOCIATED"

interface Props {
  isModal?: boolean
}

export default function RiskEditCardOpen({ isModal = false }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { backTo } = (location.state as any) || {}

  // Selectors
  const active = useAppSelector(riskStore.selectors.active)
  const draft = useAppSelector(riskStore.selectors.draft)
  const isEdited = useAppSelector(riskStore.selectors.isEdited)
  const status = useAppSelector(riskStore.selectors.status)
  const optionalRiskAttributes: string[] =
    useAppSelector(organizationStore.selectors.active)?.optionalRiskAttributes ?? []
  const units = useAppSelector(unitStore.selectors.list)
  const familyNames = useAppSelector(riskStore.selectors.listNames)
  const risks = useAppSelector(riskStore.selectors.list)

  // Map family name → rps boolean (first risk encountered wins)
  const familyRpsMap = useMemo(() => {
    const map: Record<string, boolean> = {}
    for (const r of risks) {
      if (r.name && !(r.name in map)) map[r.name] = r.rps
    }
    return map
  }, [risks])

  const activeFamily = Object.values(draft.attributes)[0] as string | undefined
  const allFamilies = familyNames
    .map((family: string) => ({ id: family, name: family, rps: familyRpsMap[family] ?? true }))
    .sort((a, b) => {
      if (a.id === activeFamily) return -1
      if (b.id === activeFamily) return 1
      return a.name.localeCompare(b.name)
    })

  //State
  const [loadingAttr, setLoadingAttr] = useState<string | null>(null)
  const [owlStates, setOwlStates] = useState<Record<string, boolean>>({})
  const [modal, setModal] = useState<string | null>(null)
  const [newFamilyInput, setNewFamilyInput] = useState<string>("")
  const [isAddFamilyModalOpen, setIsAddFamilyModalOpen] = useState<boolean>(false)
  const [suggestionModal, setSuggestionModal] = useState<{
    field: string
    oldValue: string
    newValue: string
  } | null>(null)
  const [suggestionsQueue, setSuggestionsQueue] = useState<
    Array<{ field: string; oldValue: string; newValue: string }>
  >([])
  const [suggestionId, setSuggestionId] = useState(0)
  const [updateAssociatedRisks, setUpdateAssociatedRisks] = useState(false)

  // ---- Suggestion modal ----
  function updateSuggestionModal(suggestion: { field: string; oldValue: string; newValue: string } | null) {
    setSuggestionModal(suggestion)
    if (suggestion) {
      setSuggestionId((prev) => prev + 1)
    }
  }

  // ---- gestion hiboux ----
  function startOwl(attrKey: string) {
    setOwlStates((prev) => ({ ...prev, [attrKey]: true }))
  }

  function stopOwl(attrKey: string) {
    setOwlStates((prev) => ({ ...prev, [attrKey]: false }))
  }

  function startAllOwls() {
    const allKeys = ["__root__", ...Object.keys(draft.attributes).filter((_, i) => i !== 0)]
    const updates: Record<string, boolean> = {}
    allKeys.forEach((k) => (updates[k] = true))
    setOwlStates((prev) => ({ ...prev, ...updates }))
  }

  function stopAllOwls() {
    const allKeys = ["__root__", ...Object.keys(draft.attributes).filter((_, i) => i !== 0)]
    const updates: Record<string, boolean> = {}
    allKeys.forEach((k) => (updates[k] = false))
    setOwlStates((prev) => ({ ...prev, ...updates }))
  }

  //Cancel edition
  function handleCancel() {
    dispatch(riskStore.actions.cancel())
  }

  //Click on save button
  function handleSave() {
    // Si associatedRisks et isModal, montrer la modal de confirmation
    if (isModal && draft.associatedRisks.length > 0) {
      setModal(MODAL_CONFIRM_ASSOCIATED)
    } else {
      performSave()
    }
  }

  // Perform the actual save
  async function performSave() {
    try {
      const riskToValidate = {
        ...draft,
        associatedRisks: updateAssociatedRisks ? draft.associatedRisks : [],
        ...(isModal && { dateValidated: new Date() })
      }

      // Use the updateValidated thunk to properly handle the save workflow
      await dispatch(riskStore.thunks.updateValidated([riskToValidate]))

      // Refresh the validated risks list to see the updated data
      await dispatch(riskStore.thunks.fetchValidatedAndSet(null))

      setUpdateAssociatedRisks(false)

      // Navigation après succès
      if (isModal) {
        dispatch(riskStore.actions.setActive(null))
        dispatch(riskStore.actions.sort({ key: "dateValidated", asc: false }))
        navigate("/risks", { state: { keepSort: true } })
      }

      setModal(null)
      toast(t("Mise à jour du risque réussie"))
    } catch (err) {
      console.error("❌ Erreur lors de la sauvegarde:", err)
      toast(t("Erreur lors de la sauvegarde"), { type: "error" })
    }
  }

  // ---- Suggestions IA ----
  async function fetchSuggestion(attrKey: string, attributes?: Record<string, string>) {
    if (!draft.UnitId) {
      console.warn("❌ Impossible de suggérer : aucun UnitId défini")
      return null
    }

    setLoadingAttr(attrKey)

    try {
      const suggested = await dispatch(
        riskStore.thunks.suggestOne({
          UnitId: draft.UnitId ?? "",
          attributes: attributes ?? draft.attributes,
          targetAttr: attrKey === "__root__" ? undefined : attrKey,
          rps: draft.rps ?? false
        })
      ).unwrap()

      return suggested
    } catch (err) {
      console.error("❌ Erreur lors de la suggestion du risque :", err)
      return null
    } finally {
      setLoadingAttr(null)
    }
  }

  async function handleSuggestValues(targetAttr?: string) {
    if (!draft.UnitId) {
      console.warn("❌ Impossible de suggérer : aucun UnitId défini")
      return
    }

    if (!targetAttr) {
      // Reroll all: purge all secondary, fetch all, build queue
      startAllOwls()

      // Capture old values and build cleared attributes to send to the API
      const oldValues: Record<string, string> = {}
      const clearedAttributes = { ...draft.attributes }
      Object.entries(draft.attributes).forEach(([k], idx) => {
        if (idx !== 0) {
          oldValues[k] = draft.attributes[k] ?? ""
          clearedAttributes[k] = ""
          dispatch(riskStore.actions.edit({ key: "attributes", nestedKey: k, nestedValue: "" }))
        }
      })

      const suggested = await fetchSuggestion("__root__", clearedAttributes)
      if (suggested?.attributes) {
        const queue: Array<{ field: string; oldValue: string; newValue: string }> = []

        Object.entries(suggested.attributes).forEach(([k, v]) => {
          if (k !== Object.keys(draft.attributes)[0] && v?.trim()) {
            queue.push({
              field: k,
              oldValue: oldValues[k] ?? "",
              newValue: v
            })
          }
        })

        setSuggestionsQueue(queue)
        if (queue.length > 0) {
          updateSuggestionModal(queue[0])
        }
      }

      stopAllOwls()
    } else {
      // Reroll single field
      const oldValue = draft.attributes[targetAttr] ?? ""
      setLoadingAttr(targetAttr)

      dispatch(riskStore.actions.edit({ key: "attributes", nestedKey: targetAttr, nestedValue: "" }))
      startOwl(targetAttr)

      const suggested = await fetchSuggestion(targetAttr, { ...draft.attributes, [targetAttr]: "" })
      if (suggested?.attributes) {
        const newValue = suggested.attributes[targetAttr]
        if (newValue?.trim()) {
          updateSuggestionModal({
            field: targetAttr,
            oldValue,
            newValue
          })
        }
      }

      stopOwl(targetAttr)
    }
  }

  function handleAcceptSuggestion(value: string) {
    if (!suggestionModal) return

    const { field } = suggestionModal
    dispatch(
      riskStore.actions.edit({
        key: "attributes",
        nestedKey: field,
        nestedValue: value
      })
    )

    moveToNextSuggestion()
  }

  function handleRejectSuggestion() {
    if (!suggestionModal) return

    const { field } = suggestionModal
    // Clear the field since it was purged before suggesting
    dispatch(
      riskStore.actions.edit({
        key: "attributes",
        nestedKey: field,
        nestedValue: ""
      })
    )

    moveToNextSuggestion()
  }

  async function handleRerollSuggestion() {
    if (!suggestionModal) return

    const { field, oldValue } = suggestionModal
    setLoadingAttr(field)
    startOwl(field)

    dispatch(riskStore.actions.edit({ key: "attributes", nestedKey: field, nestedValue: "" }))

    const suggested = await fetchSuggestion(field, { ...draft.attributes, [field]: "" })
    if (suggested?.attributes) {
      const newValue = suggested.attributes[field]
      if (newValue?.trim()) {
        updateSuggestionModal({
          field,
          oldValue,
          newValue
        })
      }
    }

    stopOwl(field)
  }

  function moveToNextSuggestion() {
    if (suggestionsQueue.length <= 1) {
      updateSuggestionModal(null)
      setSuggestionsQueue([])
    } else {
      const nextQueue = suggestionsQueue.slice(1)
      setSuggestionsQueue(nextQueue)
      updateSuggestionModal(nextQueue[0])
    }
  }

  return (
    <Card className="risk-edit-card risk-edit-card-open !h-fit">
      {modal === MODAL_DELETE && <RiskDeleteModal onClose={() => navigate(-1)} />}

      {modal === MODAL_HISTORY && <RiskHistorySideNav onClose={() => setModal(null)} />}

      {modal === MODAL_CONFIRM_ASSOCIATED && (
        <Modal
          isCloseButtonVisible
          onClose={() => setModal(null)}
          title="Mettre à jour les risques associés"
          onNext={() => {
            performSave()
            setModal(null)
          }}>
          <p>
            <b>{t("Ce risque est également associé aux unités de travail suivantes :")}</b>
          </p>
          <div className="mt-20 mb-20">
            {draft.associatedRisks.map((risk) => (
              <div key={risk.id} className="grey-t">
                {risk.unitName}
              </div>
            ))}
          </div>

          <div className="flex gap-4 items-center">
            <Checkbox active={updateAssociatedRisks} onClick={() => setUpdateAssociatedRisks(!updateAssociatedRisks)} />
            <div className="text-sm grey-t">
              <b>{t("Je souhaite également mettre à jour ce risque pour les unités de travail suivantes")}</b>
              {` : ${draft.associatedRisks.map((x) => x.unitName).join(", ")}`}
            </div>
          </div>
        </Modal>
      )}

      {suggestionModal && (
        <RiskSuggestionModal
          field={suggestionModal.field}
          oldValue={suggestionModal.oldValue}
          newValue={suggestionModal.newValue}
          onAccept={handleAcceptSuggestion}
          onReject={handleRejectSuggestion}
          onReroll={handleRerollSuggestion}
          isLoading={loadingAttr === suggestionModal.field}
          suggestionId={suggestionId}
        />
      )}

      <div className="flex justify-between items-center gap-4">
        <h2 className="font-bold">{active.name}</h2>
        <div className="flex gap-2">
          {/* TODO: History feature - uncomment when ready */}
          {/* <Tooltip text={t("Historique")} position="left">
            <ListButton icon={faHistory} onClick={() => setModal(MODAL_HISTORY)} />
          </Tooltip> */}
        </div>
      </div>

      <FadeIn>
        <div className="grey-t">
          <div className="flex items-center gap-2">
            <span> {t("unit")}</span>
            <b>
              <Tooltip text={`Modifier l'unité de travail : ${draft.unitName}`} position="top" width={200} usePortal>
                <div
                  onClick={() => {
                    dispatch(unitStore.actions.setActive(units.find((u) => u.id === draft.UnitId) || null))
                    navigate(`/risks/unit/${draft.UnitId}`)
                  }}>
                  <UnitBadge unitName={draft.unitName} unitSize={draft.unitSize} isCurrentUnit={true} />
                </div>
              </Tooltip>
            </b>
          </div>
          {draft.associatedRisks.filter((x) => x.UnitId !== draft.UnitId).length > 0 && (
            <div className="text-sm mt-4 space-y-2">
              <b>{t("Autres unités de travail concernées")}</b>
              <div className="flex flex-wrap gap-2">
                {[...draft.associatedRisks]
                  .filter((x) => x.UnitId !== draft.UnitId)
                  .sort((a, b) => a.unitName.localeCompare(b.unitName))
                  .map((x) => (
                    <Tooltip
                      key={x.id}
                      text={`Modifier ce risque dans l'unité de travail : ${x.unitName}`}
                      position="top"
                      width={200}
                      usePortal>
                      <div
                        onClick={() => {
                          const siblings = [
                            { ...draft, associatedRisks: [] },
                            ...draft.associatedRisks.filter((ar) => ar.id !== x.id)
                          ]
                          dispatch(riskStore.actions.setActive({ ...x, associatedRisks: siblings }))
                          navigate(`/risks/risk/${x.id}`, { state: { backTo } })
                        }}>
                        <UnitBadge unitName={x.unitName} unitSize={x.unitSize} isCurrentUnit={false} />
                      </div>
                    </Tooltip>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* RPS Toggle */}
        <div className="mt-6 flex items-center gap-3">
          <span className="text-sm font-medium">Type de risque:</span>
          <div className="flex items-center gap-2 cursor-pointer">
            <RiskTypeChip
              isRps={true}
              showCount={false}
              showLabel={true}
              disableTooltip
              color={"#999"}
              isOutlineColored={!draft.rps}
              onClick={() =>
                dispatch(
                  riskStore.actions.edit({
                    key: "rps",
                    value: true
                  })
                )
              }
            />
            <RiskTypeChip
              isRps={false}
              showCount={false}
              showLabel={true}
              disableTooltip
              color={"#999"}
              isOutlineColored={draft.rps}
              onClick={() =>
                dispatch(
                  riskStore.actions.edit({
                    key: "rps",
                    value: false
                  })
                )
              }
            />
          </div>
        </div>

        {/* Attributs */}
        <div className="flex flex-col gap-6 mt-4">
          {/* Root attr */}
          {Object.entries(draft.attributes).map(([key, value], index) => {
            if (index !== 0) return null

            return (
              <div key={key} className="flex items-center gap-2 w-[99%] relative">
                <div className="flex flex-col flex-1">
                  <div
                    className={`text-input-title whitespace-normal break-words font-bold ${(!value || (value as any)?.trim?.() === "") && !optionalRiskAttributes.includes(key) ? "red-t" : "grey-t"}`}>
                    {key}
                    {optionalRiskAttributes.includes(key) && (!value || (value as any)?.trim?.() === "") && (
                      <Tooltip text={t("Champ optionnel — non pris en compte pour la complétion")} position="top">
                        <span className="ml-1 text-gray-400 cursor-help">*</span>
                      </Tooltip>
                    )}
                  </div>
                  <Dropdown
                    active={value}
                    list={allFamilies}
                    displayField="name"
                    onSelect={(selected: any) =>
                      dispatch(
                        riskStore.actions.edit({
                          key: "attributes",
                          nestedKey: key,
                          nestedValue: selected.id
                        })
                      )
                    }
                    onAdd={() => {
                      setNewFamilyInput("")
                      setIsAddFamilyModalOpen(true)
                    }}
                    renderItemStart={(item) => (
                      <RiskTypeChip isRps={item.rps} showCount={false} showLabel={false} isSmall />
                    )}
                    value={value ?? ""}
                    isFullWidth
                  />
                </div>
              </div>
            )
          })}

          {/* Autres attrs */}
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(draft.attributes).map(([key, value], index) => {
              if (index === 0) return null
              const rootValue = Object.values(draft.attributes)[0]
              const hasRoot = !!(rootValue as any)?.trim?.()

              return (
                <div key={key} className="text-input text-input-fullwidth relative flex flex-col justify-end">
                  <div className="flex justify-between items-center">
                    <div
                      className={`text-input-title whitespace-normal break-words font-bold ${(!value || (value as any)?.trim?.() === "") && !optionalRiskAttributes.includes(key) ? "red-t" : "grey-t"}`}>
                      {key}
                      {optionalRiskAttributes.includes(key) && (!value || (value as any)?.trim?.() === "") && (
                        <Tooltip text={t("Champ optionnel — non pris en compte pour la complétion")} position="top">
                          <span className="ml-1 text-gray-400 cursor-help">*</span>
                        </Tooltip>
                      )}
                    </div>

                    {hasRoot && (
                      <Tooltip text={t(`Compléter le champ « ${key} » avec une suggestion`)} position="left">
                        <div
                          onClick={() => {
                            if (!loadingAttr) {
                              handleSuggestValues(key)
                            }
                          }}
                          className="flex items-center justify-center w-7 h-7 rounded-full transition cursor-pointer hover:scale-110"
                          style={{ border: "1.5px solid #9ca3af", color: "#6b7280" }}>
                          {loadingAttr === key ? (
                            <FontAwesomeIcon icon={faSpinner} spin className="text-sm" />
                          ) : (
                            <FontAwesomeIcon icon={faWandMagicSparkles} className="text-sm" />
                          )}
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  <div className="absolute -bottom-0 right-2">
                    <OwlRunner loading={!!owlStates[key]} />
                  </div>

                  <textarea
                    className="text-area"
                    disabled={!!owlStates[key]}
                    value={value ?? ""}
                    onChange={(e) =>
                      dispatch(
                        riskStore.actions.edit({
                          key: "attributes",
                          nestedKey: key,
                          nestedValue: e.target.value
                        })
                      )
                    }
                    rows={3}
                  />
                </div>
              )
            })}
          </div>
        </div>

        {/* Échelles */}
        <div className="flex gap-4 mt-6">
          <div className="flex flex-col w-full">
            <RiskScaleSelector id="severity" helpKey="g" />
            <RiskScaleSelector id="frequency" helpKey="f" />
            <RiskScaleSelector id="prevention" helpKey="p" />
          </div>
        </div>

        <hr className="my-6" />

        {/* Brut + Résiduel */}
        <div className="flex justify-around my-6">
          <div className="flex flex-col items-center flex-1 gap-4">
            <div className="flex items-center gap-2">
              <span className="text-input-title grey-t font-bold">{t("risk_item_rb")}</span>
              <Tooltip text={getRiskHelpText("rb", t)} position="top">
                <FontAwesomeIcon className="cursor-help text-gray-500" icon={faInfoCircle} />
              </Tooltip>
            </div>
            <Chip isOutlineColored color={draft.valueInitialColor} className="scale-150">
              {draft.valueInitialLabel}
            </Chip>
          </div>

          <div className="flex flex-col items-center flex-1 gap-4">
            <div className="flex items-center gap-2">
              <span className="text-input-title grey-t font-bold">{t("risk_item_rr")}</span>
              <Tooltip text={getRiskHelpText("rr", t)} position="top">
                <FontAwesomeIcon className="cursor-help text-gray-500" icon={faInfoCircle} />
              </Tooltip>
            </div>
            <Chip color={draft.valueEffectiveColor} className="scale-150 mb-4">
              {draft.valueEffectiveLabel}
            </Chip>
          </div>
        </div>

        {isAddFamilyModalOpen && (
          <Modal
            title={`Ajouter un(e) ${Object.keys(draft.attributes)?.[0] || "famille"}`}
            isCloseButtonVisible
            disableKeyEvents
            onClose={() => {
              setIsAddFamilyModalOpen(false)
              setNewFamilyInput("")
            }}
            onNext={() => {
              if (newFamilyInput.trim()) {
                dispatch(
                  riskStore.actions.edit({
                    key: "attributes",
                    nestedKey: Object.keys(draft.attributes)[0],
                    nestedValue: newFamilyInput.trim()
                  })
                )
                setIsAddFamilyModalOpen(false)
                setNewFamilyInput("")
              }
            }}
            nextDisabled={!newFamilyInput.trim()}
            onNextCta="Ajouter">
            <div style={{ minWidth: "300px" }}>
              <TextInput
                title={`Nom du nouveau ${Object.keys(draft.attributes)?.[0] || "champ"}`}
                value={newFamilyInput}
                onChange={(e: any) => setNewFamilyInput(e.value)}
                onKeyDown={(key: string) => {
                  if (key === "Enter" && newFamilyInput.trim()) {
                    dispatch(
                      riskStore.actions.edit({
                        key: "attributes",
                        nestedKey: Object.keys(draft.attributes)[0],
                        nestedValue: newFamilyInput.trim()
                      })
                    )
                    setIsAddFamilyModalOpen(false)
                    setNewFamilyInput("")
                  }
                }}
                autoFocus
              />
            </div>
          </Modal>
        )}

        {modal !== MODAL_SCALE_CONFIG && (
          <SaveToast
            status={status}
            isSaveActive={(isEdited && draft.name !== "") || draft.options?.askSave || (location.state as any)?.askSave}
            onCancel={() => handleCancel()}
            onDelete={() => setModal(MODAL_DELETE)}
            onSave={() => handleSave()}
            onSaveTooltip={
              !draft.attributes || !Object.values(draft.attributes)[0]
                ? t(`Renseignez « ${Object.keys(draft.attributes)[0] ?? "…"} » pour pouvoir sauvegarder`)
                : undefined
            }
          />
        )}
      </FadeIn>
    </Card>
  )
}
